import { getFileName } from '@com/utils/file'
import { ActionLogData } from '@com/utils/report-logs'
import { veproLogs } from '@com/utils/report-logs/ve-pro'
import { AssetStatus } from '@ve-pro/enums/asset'
import { SegmentMaterial } from '@ve-pro/types/track.type'
import { db } from '@com/web-database/indexeddb'
import { Asset } from '@com/web-database/type'
import { checkAssetProgress } from './asset-progress'
import { buffer2Blob } from '@com/utils/blob'

export interface AssetManagerLogOptions {
  enableLog?: boolean
  logAction?: ReturnType<typeof veproLogs.createAction>
  asset?: SegmentMaterial
}

type AssetUrl =
  | SegmentMaterial['file_url']
  | SegmentMaterial['out_mp3_url']
  | SegmentMaterial['out_mp4_url']
  | SegmentMaterial['out_mp4_preview_url']

export class AssetManager {
  private cached = new Map<AssetUrl, Promise<ArrayBuffer>>()

  async getBlobUrl(
    asset: SegmentMaterial,
    key: keyof Pick<
      SegmentMaterial,
      'file_url' | 'out_mp3_url' | 'out_mp4_url' | 'out_mp4_preview_url'
    >,
  ): Promise<string> {
    const action = veproLogs.createAction()
    const url = await this.getUrlByAsset(asset, key, {
      enableLog: true,
      logAction: action,
    })
    return buffer2Blob(
      await this.get(url, undefined, { enableLog: true, logAction: action }),
    )
  }

  // 获取某个资源的指定的url
  // 原因是为了检测某个资源是否还未转码成功
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, consistent-return
  async getUrlByAsset(
    asset: SegmentMaterial,
    key: keyof Pick<
      SegmentMaterial,
      'file_url' | 'out_mp3_url' | 'out_mp4_url' | 'out_mp4_preview_url'
    >,
    options?: AssetManagerLogOptions,
  ): Promise<AssetUrl> {
    const { enableLog, logAction } = options ?? {}

    let action: ReturnType<typeof veproLogs.createAction> | undefined

    if (enableLog) {
      action = logAction ?? veproLogs.createAction()
    }

    if (asset.status === AssetStatus.Active) {
      return asset[key]
    }

    const t = Date.now()
    action?.log({
      e: 'wait_t',
      et: 's',
      extra: {
        md5: asset.file_md5,
        asset_id: asset.asset_id,
        asset_source: asset.source,
      },
    })

    const task = checkAssetProgress(asset)

    const setEndLog = (status: ActionLogData['status']) => {
      const endTime = Date.now()
      action?.log({
        e: 'wait_t',
        et: 'e',
        d: endTime - t,
        extra: {
          md5: asset.file_md5,
          asset_id: asset.asset_id,
          asset_source: asset.source,
        },
        status,
      })
    }
    task
      .then(() => {
        setEndLog(2)
      })
      .catch(() => {
        setEndLog(1)
      })

    const progress = await task

    // progress
    // asset.status = progress.status
    // asset.file_url = progress.file_url
    // asset.out_mp3_url = progress.out_mp3_url
    // asset.out_mp4_url = progress.out_mp4_url

    if (progress.status === AssetStatus.Active) {
      return progress[key]
    }

    return Promise.reject()
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, consistent-return
  async getByAsset(
    asset: SegmentMaterial,
    key: keyof Pick<
      SegmentMaterial,
      'file_url' | 'out_mp3_url' | 'out_mp4_url' | 'out_mp4_preview_url'
    >,
    options?: AssetManagerLogOptions,
  ) {
    const { enableLog = true, logAction } = options ?? {}

    const action: ReturnType<typeof veproLogs.createAction> =
      logAction ?? veproLogs.createAction()

    const url = await this.getUrlByAsset(asset, key, {
      enableLog: enableLog,
      logAction: action,
    })

    return this.get(url, undefined, {
      enableLog: enableLog,
      logAction: action,
    })
  }

  async get(
    url: AssetUrl,
    md5?: SegmentMaterial['file_md5'],
    options?: AssetManagerLogOptions,
  ): Promise<ArrayBuffer> {
    const id = md5 || getFileName(url, true)

    const value = await this._getByDB(id).catch(() => {
      return Promise.reject()
    })
    if (typeof value !== 'undefined') return value.data

    const fetchTask = this._fetch(url)

    fetchTask.catch(() => {
      return Promise.reject()
    })

    const { enableLog, logAction, asset } = options ?? {}

    if (enableLog) {
      // 记录下载日志
      const action = logAction ?? veproLogs.createAction()

      const t = Date.now()
      action.log({
        e: 'dl',
        et: 's',
        extra: {
          md5: id,
          asset_id: asset?.asset_id ?? asset?.id,
          asset_source: asset?.source,
        },
      })
      const setEndLog = (status: ActionLogData['status'], size?: number) => {
        const endTime = Date.now()
        action?.log({
          e: 'dl',
          et: 'e',
          d: endTime - t,
          extra: {
            md5: id,
            asset_id: asset?.asset_id ?? asset?.id,
            asset_source: asset?.source,
            size: size ?? 0,
          },
          status,
        })
      }
      fetchTask
        .then((buffer) => {
          setEndLog(2, buffer.byteLength)
        })
        .catch(() => {
          setEndLog(1)
        })
    }
    const data = await fetchTask
    // TODO 解决因为DB超出浏览器限制，导致页面奔溃的bug
    // 暂时注释掉存储到DB的操作，如果需要缓存，需要提供一个LRU算法和指定最大存储内容的限制（1G）
    // db.asset.add({
    //   id,
    //   data,
    //   size: data.byteLength,
    //   updated_at: Date.now(),
    // })

    this.cached.delete(url)

    return data
  }

  delete(url: AssetUrl): Promise<void> {
    const id = getFileName(url, true)

    this.cached.delete(url)
    return db.asset.delete(id)
  }

  async getByDB(
    id: SegmentMaterial['file_md5'],
  ): Promise<Asset<ArrayBuffer> | undefined> {
    return this._getByDB(id)
  }

  private async _getByDB(
    id: SegmentMaterial['file_md5'],
  ): Promise<Asset<ArrayBuffer> | undefined> {
    const res = await db.asset.get({ id })
    console.log(res)

    // 更新资源的使用时间
    if (res) {
      db.asset.update(id, {
        updated_at: Date.now(),
      })
    }

    return res
  }

  private async _fetch(url: AssetUrl): Promise<ArrayBuffer> {
    const buffer = this.cached.get(url)

    if (typeof buffer !== 'undefined') return buffer

    const request = fetch(url, {
      credentials: 'same-origin',
      // cache: 'no-store',
    }).then((res) => {
      if (res.status !== 200) {
        return Promise.reject()
      }
      return res.arrayBuffer()
    })

    this.cached.set(url, request)

    return request
  }
}

export const assetManager = new AssetManager()
